import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { GameLogMessage } from "./GameLogMessage";
import { ComponentComp } from "../GameComponent";
import { LogMessageData, PlayerColorInfo } from "src/components/types";
import "./GameLog.css";
import { scrollSmoothTargetElmToCenterOfParentElm } from "src/components/gametable/redesign/utility";

type GameLogProps = {
  messages: LogMessageData[];
  playerColors?: PlayerColorInfo[];
  getImage: (componentName: string) => string | undefined;
  logOpen: boolean;
  currEventNumber: number;
  tableStatus: string;
  moveToEvent: (eventNumber: number) => void;
  createDebugTable: (eventNumber: number) => void;
  globalAssets?: { [key: string]: any };
};

export function GameLog({
  messages,
  playerColors,
  getImage,
  logOpen,
  currEventNumber,
  tableStatus,
  moveToEvent,
  createDebugTable,
  globalAssets,
}: GameLogProps) {
  const [messageSelected, setMessageSelected] = useState(-1);
  const [fullLog, setFullLog] = useState<LogMessageData[]>([]);
  const [turnIdToTurnSequenceMap, setTurnIdToTurnSequenceMap] = useState<Map<number, number>>(new Map());
  const [maxTurnSequence, setMaxTurnSequence] = useState<number>(0);

  const logEndRef = useRef<HTMLDivElement>(null);
  const currentTurnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tableStatus !== "ended" && logEndRef.current) {
      scrollSmoothTargetElmToCenterOfParentElm(logEndRef.current.parentElement as HTMLElement, logEndRef.current);
    } else if (tableStatus === "ended" && currentTurnRef.current) {
      scrollSmoothTargetElmToCenterOfParentElm(
        currentTurnRef.current.parentElement as HTMLElement,
        currentTurnRef.current,
      );
    } else if (tableStatus === "ended" && currEventNumber >= maxTurnSequence && logEndRef.current) {
      scrollSmoothTargetElmToCenterOfParentElm(logEndRef.current.parentElement as HTMLElement, logEndRef.current);
    }
  }, [logOpen, currEventNumber, tableStatus, maxTurnSequence, fullLog]);

  useEffect(() => {
    if (tableStatus == "ended") {
      if (messages.length > fullLog.length) {
        let messagesCopy = [...messages];
        const newTurnIdToTurnSequenceMap = new Map<number, number>();
        let sequentialNumber = 0;
        let messagesWithMessage = messagesCopy.filter(
          (msg) => msg.message?.trim().length && msg.currTurn !== undefined,
        );
        messagesWithMessage.forEach((msg) => {
          newTurnIdToTurnSequenceMap.set(msg.currTurn ?? -1, sequentialNumber);
          sequentialNumber++;
        });
        setTurnIdToTurnSequenceMap(newTurnIdToTurnSequenceMap);
        setMaxTurnSequence(sequentialNumber);

        setFullLog(messagesCopy);
      }
    } else {
      let messagesCopy = [...messages];
      setFullLog(messagesCopy);
    }
  }, [messages, tableStatus]);

  const setLogDetail = useCallback((i: string) => {
    setMessageSelected(+i);
  }, []);

  let messageElements: ReactNode[] = [];
  let currIndent = 0;

  let currentAssociatedEventNumber = 0;
  let previousAssociatedEventNumber = -1;
  let isTurnHeader = false;
  let duplicateTurnCount = 0;

  for (let i = 0; i < fullLog.length; ++i) {
    let logMessageData = fullLog[i];
    let displayTimestamp = 0;
    if (Object.hasOwn(logMessageData.event, "systemTurn")) {
      displayTimestamp = logMessageData.event.timestamp as number;
    }

    if (logMessageData.message?.trim().length) {
      if (tableStatus === "ended" && logMessageData.currTurn !== undefined) {
        currentAssociatedEventNumber = turnIdToTurnSequenceMap.get(logMessageData.currTurn) ?? -1;
        if (currentAssociatedEventNumber !== previousAssociatedEventNumber) {
          previousAssociatedEventNumber = currentAssociatedEventNumber;
          isTurnHeader = true;
        }
      }
      // event number is counted differently for tables that haven't ended
      if (tableStatus !== "ended" && logMessageData.currTurn) {
        if (logMessageData.currTurn + duplicateTurnCount === currentAssociatedEventNumber) {
          duplicateTurnCount++;
        }
        currentAssociatedEventNumber = logMessageData.currTurn + duplicateTurnCount;
      }

      messageElements.push(
        <GameLogMessage
          logMessageData={logMessageData}
          indent={currIndent}
          displayTimestamp={displayTimestamp ?? 0}
          messageIndex={i}
          selected={+messageSelected === i}
          setLogDetail={setLogDetail}
          playerColors={playerColors}
          getImage={getImage}
          key={"message-" + i}
          associatedEventNumber={currentAssociatedEventNumber}
          isTurnHeader={isTurnHeader}
          currentTurnRef={currentTurnRef}
          moveToEvent={tableStatus == "ended" ? moveToEvent : undefined}
          createDebugTable={createDebugTable}
          tableStatus={tableStatus}
          globalAssets={globalAssets}
          isFutureTurn={currEventNumber <= currentAssociatedEventNumber}
          isCurrentTurn={currEventNumber === currentAssociatedEventNumber}
        />,
      );
      isTurnHeader = false;
    }

    if (logMessageData.event.logIndent) {
      currIndent += logMessageData.event.logIndent;
    }

    if (logMessageData.event.type === "reset indent") {
      currIndent = 0;
    }
  }

  let selectedMessage = fullLog[messageSelected];
  let componentData;
  let previewComp = <></>;
  if (selectedMessage) {
    componentData = Object.assign({}, selectedMessage.logDisplayComponent);
    if (componentData.name !== "unknown") {
      componentData.animationData = undefined;
      if (Object.keys(componentData).length > 1) {
        componentData.animationData = undefined;
        previewComp = (
          <div className="log-preview">
            <ComponentComp
              data={componentData}
              x={0}
              y={0}
              scaleMult={0.5}
              width={200}
              height={200}
              preview={true}
              logPreview={true}
            />
          </div>
        );
      }
    }
  }

  return (
    <div className={"game-log" + (logOpen ? " open" : "")}>
      {previewComp}
      <div className="game-log-messages">
        {messageElements}
        <div ref={logEndRef} key="logend" id="logend" />
      </div>
    </div>
  );
}
